import React, { useEffect, useMemo, useState } from "react";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { AiOutlineFileAdd, AiOutlineSearch } from "react-icons/ai";
import Sidebaradmin from "./Sidebar";
import Navbaradmin from "./navbar";
import CreatePostForm from "./CreatePost";

const initialPosts = [];

const statusClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "published") return "bg-emerald-50 text-emerald-700 ring-emerald-100";
  if (normalized === "draft") return "bg-amber-50 text-amber-700 ring-amber-100";
  return "bg-slate-100 text-slate-600 ring-slate-200";
};

const postDate = (post) => {
  const date = post?.date || post?.createdAt || post?.updatedAt;
  return date ? new Date(date).toLocaleDateString() : "N/A";
};

export default function Post() {
  const [posts, setPosts] = useState(initialPosts);
  const [search, setSearch] = useState("");
  const [editingPost, setEditingPost] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch("/api/admin/posts");
        const data = await response.json();
        setPosts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching posts:", error);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = useMemo(
    () =>
      posts.filter((post) => {
        const haystack = `${post?.title || ""} ${post?.content || ""} ${post?.excerpt || ""}`.toLowerCase();
        return haystack.includes(search.toLowerCase());
      }),
    [posts, search]
  );

  const publishedCount = posts.filter((post) => String(post?.status).toLowerCase() === "published").length;
  const draftCount = posts.filter((post) => String(post?.status).toLowerCase() === "draft").length;

  const handleAddPost = async (newPost) => {
    try {
      const response = await fetch("/api/posts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPost),
      });
      const addedPost = await response.json();
      setPosts([...posts, addedPost]);
      setEditingPost(null);
      setPreviewMode(false);
    } catch (error) {
      console.error("Error adding post:", error);
    }
  };

  const handleUpdatePost = async (updatedPost) => {
    try {
      const response = await fetch(`/api/posts/${editingPost._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updatedPost),
      });
      const data = await response.json();
      setPosts(posts.map((post) => (post._id === data._id ? { ...data } : post)));
      setEditingPost(null);
      setPreviewMode(false);
    } catch (error) {
      console.error("Error updating post:", error);
    }
  };

  const handleDeletePost = async (id) => {
    const shouldDelete = window.confirm("Are you sure you want to delete this post?");
    if (!shouldDelete) return;

    try {
      await fetch(`/api/posts/${id}`, {
        method: "DELETE",
      });
      setPosts(posts.filter((post) => post._id !== id));
      if (editingPost?._id === id) {
        setEditingPost(null);
      }
    } catch (error) {
      console.error("Error deleting post:", error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen flex-col md:flex-row">
        <Sidebaradmin />

        <div className="flex min-w-0 flex-1 flex-col">
          <Navbaradmin />

          <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <section className="mb-6 flex flex-col justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-sky-600">Content management</p>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Admin Posts
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">
                  Create, edit, preview, publish, and manage blog content from one workspace.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:min-w-[420px]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase text-slate-500">Total</p>
                  <p className="mt-1 text-2xl font-bold text-slate-950">{posts.length}</p>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-xs font-semibold uppercase text-emerald-700">Published</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-800">{publishedCount}</p>
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
                  <p className="text-xs font-semibold uppercase text-amber-700">Drafts</p>
                  <p className="mt-1 text-2xl font-bold text-amber-800">{draftCount}</p>
                </div>
              </div>
            </section>

            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <label className="relative flex-1">
                  <AiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xl text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search posts by title, content, or excerpt"
                    className="h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                <button
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-sky-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
                  onClick={() => {
                    setEditingPost({
                      title: "",
                      content: "",
                      excerpt: "",
                      author: "",
                      status: "draft",
                      category: "Latest",
                    });
                    setPreviewMode(false);
                  }}
                >
                  <AiOutlineFileAdd className="text-lg" />
                  New Post
                </button>
              </div>
            </section>

            {editingPost && (
              <CreatePostForm
                post={editingPost}
                onSubmit={editingPost._id ? handleUpdatePost : handleAddPost}
                onCancel={() => {
                  setEditingPost(null);
                  setPreviewMode(false);
                }}
                previewMode={previewMode}
                setPreviewMode={setPreviewMode}
              />
            )}

            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">Post Library</h2>
                  <p className="text-sm text-slate-500">Manage published articles and drafts.</p>
                </div>
                <span className="rounded-full bg-sky-50 px-3 py-1.5 text-sm font-semibold text-sky-700 ring-1 ring-sky-100">
                  {filteredPosts.length} shown
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-left text-sm">
                  <thead>
                    <tr className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3 font-semibold">Title</th>
                      <th className="px-4 py-3 font-semibold">Category</th>
                      <th className="px-4 py-3 font-semibold">Author</th>
                      <th className="px-4 py-3 font-semibold">Date</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredPosts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                          No posts found.
                        </td>
                      </tr>
                    ) : (
                      filteredPosts.map((post) => (
                        <tr key={post._id || post.title} className="text-slate-700 transition hover:bg-slate-50">
                          <td className="max-w-md px-4 py-4">
                            <div className="font-semibold text-slate-950">{post.title || "Untitled post"}</div>
                            {post.excerpt && <p className="mt-1 line-clamp-1 text-xs text-slate-500">{post.excerpt}</p>}
                          </td>
                          <td className="px-4 py-4">{post.category || "Latest"}</td>
                          <td className="px-4 py-4">{post.author || "Admin"}</td>
                          <td className="px-4 py-4">{postDate(post)}</td>
                          <td className="px-4 py-4">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass(post.status)}`}>
                              {post.status || "draft"}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
                                onClick={() => {
                                  setEditingPost(post);
                                  setPreviewMode(false);
                                }}
                                aria-label="Edit post"
                              >
                                <PencilSquareIcon className="h-5 w-5" />
                              </button>
                              <button
                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                                onClick={() => handleDeletePost(post._id)}
                                aria-label="Delete post"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
}
